import type { RailStep } from "./rail-model";

// F39 (Pavel 2026-08-04): "Pokud přijde notifikace, že se cokoliv otevřelo,
// chci to vidět otevřené v levém menu BEZ aktualizace stránky. A poprvé
// pulzující - třeba ten otevřený zámeček, ať si toho všimnu a mám radost. Po
// prvním kliknutí může zmizet."
//
// The pulse marks steps that opened SINCE the client last looked. The seen
// set is the acknowledgement ledger: a key enters it when the client clicks
// the step (or, on the very first visit, as the silent baseline - otherwise
// a brand new client would meet a rail pulsing at every open step at once).
const STORAGE_PREFIX = "launchpad-seen-unlocked";

export function seenUnlockedStorageKey(clientId: string): string {
  return `${STORAGE_PREFIX}-${clientId}`;
}

// A step is "open" while it is actionable: neither still locked nor already
// finished. A finished step has nothing left to celebrate.
export function openStepKeys(steps: RailStep[]): string[] {
  return steps.filter((step) => step.state !== "locked" && step.state !== "done").map((step) => step.key);
}

// Keys that deserve the pulse right now. Stays stable across reloads until
// the client actually clicks the step, so the discovery cannot be missed by
// a background refresh.
export function freshUnlockedKeys(seen: string[] | null, steps: RailStep[]): string[] {
  if (seen === null) return [];
  const acknowledged = new Set(seen);
  return openStepKeys(steps).filter((key) => !acknowledged.has(key));
}

export function readSeenUnlocked(clientId: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(seenUnlockedStorageKey(clientId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : null;
  } catch {
    // Private mode: no ledger means no pulse, never a crash.
    return null;
  }
}

export function writeSeenUnlocked(clientId: string, seen: string[]): void {
  try {
    window.localStorage.setItem(seenUnlockedStorageKey(clientId), JSON.stringify([...new Set(seen)]));
  } catch {
    /* private mode: no ledger, no crash */
  }
}

// First visit for this client: remember what was already open so the rail
// starts calm, and only later openings pulse.
export function ensureSeenBaseline(clientId: string, steps: RailStep[]): string[] {
  const stored = readSeenUnlocked(clientId);
  if (stored) return stored;
  const baseline = openStepKeys(steps);
  writeSeenUnlocked(clientId, baseline);
  return baseline;
}

export function acknowledgeStep(seen: string[], key: string): string[] {
  return seen.includes(key) ? seen : [...seen, key];
}
